///
/// EarthBrowser
///
/// Created by Matt Giger on 11/21/2025
/// Copyright (c) 2025 Geodata Labs
///

'use client'

import { hapticTick } from '@/native/haptics'
import { setAnimatedCameraTilt, setCameraBearing } from '@/redux/earthBrowserSlice'
import { appStore, useAppDispatch, useAppSelector } from '@/redux/store'
import { cn } from '@/utils/utils'
import { useEffect, useRef, useState } from 'react'

interface CompassControlProps {
	className?: string
	size?: number
}

export function CompassControl({ className, size = 50 }: CompassControlProps) {
	const dispatch = useAppDispatch()
	const bearing = useAppSelector((state) => state.earthbrowser.cameraState.bearing)
	const canvasRef = useRef<HTMLCanvasElement>(null)
	const [isHovered, setIsHovered] = useState(false)

	const getCardinalDirection = (bearingRad: number): string => {
		// Normalize bearing to 0-360 range
		const normalizedBearing = ((bearingRad * 180 / Math.PI) % 360 + 360) % 360

		// Determine which direction we're facing (8 directions)
		if (normalizedBearing >= 337.5 || normalizedBearing < 22.5) {
			return 'N'
		} else if (normalizedBearing >= 22.5 && normalizedBearing < 67.5) {
			return 'NE'
		} else if (normalizedBearing >= 67.5 && normalizedBearing < 112.5) {
			return 'E'
		} else if (normalizedBearing >= 112.5 && normalizedBearing < 157.5) {
			return 'SE'
		} else if (normalizedBearing >= 157.5 && normalizedBearing < 202.5) {
			return 'S'
		} else if (normalizedBearing >= 202.5 && normalizedBearing < 247.5) {
			return 'SW'
		} else if (normalizedBearing >= 247.5 && normalizedBearing < 292.5) {
			return 'W'
		} else {
			return 'NW'
		}
	}

	const rotatePoint = (x: number, y: number, cx: number, cy: number, angle: number): [number, number] => {
		const cos = Math.cos(angle)
		const sin = Math.sin(angle)
		const dx = x - cx
		const dy = y - cy
		return [
			cx + dx * cos - dy * sin,
			cy + dx * sin + dy * cos
		]
	}

	const handleClick = () => {
		hapticTick('light')
		// Reset both bearing (north-up) and tilt (looking straight down)
		if(Math.abs(appStore.getState().earthbrowser.cameraState.bearing) < 0.01) {
			dispatch(setAnimatedCameraTilt(0))
		} else {
			dispatch(setCameraBearing(0))
		}
	}

	useEffect(() => {
		const canvas = canvasRef.current
		if (!canvas) return

		const ctx = canvas.getContext('2d')
		if (!ctx) return

		// Set canvas size for retina displays
		const dpr = window.devicePixelRatio || 1
		canvas.width = size * dpr
		canvas.height = size * dpr
		ctx.scale(dpr, dpr)

		// Clear canvas
		ctx.clearRect(0, 0, size, size)

		const cx = size / 2
		const cy = size / 2
		const radius = Math.min(size, size) / 2 - 4

		// Subtle background on hover
		if (isHovered) {
			ctx.beginPath()
			ctx.arc(cx, cy, radius, 0, Math.PI * 2)
			ctx.fillStyle = 'rgba(255, 255, 255, 0.125)'
			ctx.fill()
		}

		// Border circle
		ctx.beginPath()
		ctx.arc(cx, cy, radius, 0, Math.PI * 2)
		ctx.strokeStyle = 'rgba(255, 255, 255, 0.25)'
		ctx.lineWidth = 1.5
		ctx.stroke()

		// Draw tick marks - fewer for smaller sizes
		const tickInterval = size < 40 ? 30 : 10
		for (let i = 0; i < 360; i += tickInterval) {
			const angle = (i - 90) * Math.PI / 180
			const isCardinal = i % 90 === 0
			const isMajor = i % 30 === 0

			const outerRadius = radius - (size < 40 ? 1 : 2)
			const innerRadius = isCardinal ? radius - (size < 40 ? 4 : 8) : (isMajor ? radius - (size < 40 ? 3 : 6) : radius - (size < 40 ? 2 : 4))
			const strokeWidth = isCardinal ? (size < 40 ? 1.5 : 2) : (isMajor ? 1.5 : 1)

			const x1 = cx + Math.cos(angle) * outerRadius
			const y1 = cy + Math.sin(angle) * outerRadius
			const x2 = cx + Math.cos(angle) * innerRadius
			const y2 = cy + Math.sin(angle) * innerRadius

			ctx.beginPath()
			ctx.moveTo(x1, y1)
			ctx.lineTo(x2, y2)
			ctx.strokeStyle = 'rgba(255, 255, 255, 0.867)'
			ctx.lineWidth = strokeWidth
			ctx.stroke()
		}

		// Draw red north indicator arrow (rotates to point north)
		const arrowSize = size < 40 ? 5 : 8
		const arrowY = cy - radius + (size < 40 ? 5 : 8)

		// Calculate arrow points and rotate them by the bearing
		const [tipX, tipY] = rotatePoint(cx, arrowY - arrowSize, cx, cy, bearing)
		const [leftX, leftY] = rotatePoint(cx - arrowSize / 2, arrowY + arrowSize / 3, cx, cy, bearing)
		const [midX, midY] = rotatePoint(cx, arrowY, cx, cy, bearing)
		const [rightX, rightY] = rotatePoint(cx + arrowSize / 2, arrowY + arrowSize / 3, cx, cy, bearing)

		ctx.beginPath()
		ctx.moveTo(tipX, tipY)
		ctx.lineTo(leftX, leftY)
		ctx.lineTo(midX, midY)
		ctx.lineTo(rightX, rightY)
		ctx.closePath()
		ctx.fillStyle = 'rgba(255, 68, 68, 1)'
		ctx.fill()
		ctx.strokeStyle = 'white'
		ctx.lineWidth = size < 40 ? 0.5 : 1
		ctx.stroke()

		// Draw cardinal direction letter in the center
		const direction = getCardinalDirection(bearing)
		const fontSize = size < 40 ? 12 : 14
		ctx.font = `bold ${fontSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`
		ctx.fillStyle = 'white'
		ctx.textAlign = 'center'
		ctx.textBaseline = 'middle'
		ctx.fillText(direction, cx, cy)

	}, [bearing, isHovered, size])

	return (
		<button
			onClick={handleClick}
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}
			className={cn(
				"rounded-lg transition-colors duration-200",
				"hover:bg-white/10 active:bg-white/20",
				"focus:outline-none focus-visible:ring-2 focus-visible:ring-white/50",
				className
			)}
			style={{ width: size, height: size }}
			aria-label="Reset camera to north"
			title="Click to face north"
		>
			<canvas
				ref={canvasRef}
				style={{ width: size, height: size }}
				className="pointer-events-none"
			/>
		</button>
	)
}
